/* =====================================================================================
   Enriching the mapped sites with what the database knows, for search.

   The Atlas renders from the tenant seed, which holds volumes, tiers and incumbents. The
   technical picture, what a laboratory actually runs and what it sends away, lives in Postgres
   and until now was fetched one site at a time when somebody opened a record.

   Search needs it for all of them at once, because "sites that send work out" is not a question
   you can answer a site at a time. Two views already aggregate it, so this is two queries rather
   than a new table, and it degrades to the seed alone when there is no backend.

   IDS ARE CONVERTED, NOT ASSUMED. The seed identifies a centre by slug and Postgres by uuid, so
   the join runs through the same deterministic conversion every other site-scoped write uses.
   Getting that wrong returns zero rows and looks exactly like "no technical data has been
   recorded", which is the failure this product has already had once.
   ===================================================================================== */

import { BACKEND } from './db';
import { dbSiteId } from './ids';
import { TENANT } from '@tenant';
import type { Site } from '../data/atlas';
import type { SearchableSite } from './searchFields';

const live = () => BACKEND === 'supabase';

export async function loadSearchableSites(): Promise<SearchableSite[]> {
  const base = (TENANT.seed.site as Site[]).map((s) => ({
    ...s,
    kinds: [...(s.kinds ?? [])],
  })) as unknown as SearchableSite[];

  if (!live()) return base;

  try {
    const { supabase } = await import('./supabase');

    const [tech, addressable, complete] = await Promise.all([
      supabase().schema('atlas').from('v_technical_readiness')
        .select('site_id, has_long_read, sequencers, hla_typing_methods, accreditations,'
              + ' annual_typings, readiness'),
      supabase().schema('atlas').from('v_site_addressable')
        .select('site_id, realistic_annual_value'),
      /* How much is known about each site, and what is not. Loaded with the rest so the target
         list can rank on it: an incomplete record should cost something, or nobody fills it. */
      supabase().schema('atlas').from('v_site_completeness')
        .select('site_id, completeness, missing'),
    ]);

    const byId = new Map<string, Record<string, unknown>>();
    for (const r of (tech.data ?? []) as unknown as Record<string, unknown>[]) {
      byId.set(String(r.site_id), r);
    }
    /* One site can have several assay classes in the addressable view, so the values are summed
       rather than last-one-wins, which would silently report one assay's worth as the total. */
    const value = new Map<string, number>();
    for (const r of (addressable.data ?? []) as unknown as Record<string, unknown>[]) {
      const id = String(r.site_id);
      const v = Number(r.realistic_annual_value ?? 0);
      if (Number.isFinite(v)) value.set(id, (value.get(id) ?? 0) + v);
    }

    /* Send-outs live on the technical record rather than the readiness view, and are worth a
       third query because "who is already paying somebody else" is the single most commercially
       useful thing in the whole technical profile. */
    const sendOut = new Map<string, string[]>();
    const st = await supabase().schema('atlas').from('site_technical')
      .select('site_id, send_out_tests');
    for (const r of (st.data ?? []) as unknown as Record<string, unknown>[]) {
      const arr = r.send_out_tests;
      if (Array.isArray(arr) && arr.length) sendOut.set(String(r.site_id), arr.map(String));
    }

    const comp = new Map<string, { pct: number; missing: string[] }>();
    for (const r of (complete.data ?? []) as unknown as Record<string, unknown>[]) {
      comp.set(String(r.site_id), {
        pct: Number(r.completeness ?? 0),
        missing: Array.isArray(r.missing) ? r.missing.map(String) : [],
      });
    }

    return base.map((s) => {
      const id = dbSiteId(s.id);
      const t = id ? byId.get(id) : undefined;
      const c = id ? comp.get(id) : undefined;
      return {
        ...s,
        hasLongRead: t ? (t.has_long_read as boolean | null) : undefined,
        sequencers: t ? ((t.sequencers as string[]) ?? []) : undefined,
        hlaMethods: t ? ((t.hla_typing_methods as string[]) ?? []) : undefined,
        accreditations: t ? ((t.accreditations as string[]) ?? []) : undefined,
        annualTypings: t ? (t.annual_typings as number | null) : undefined,
        readiness: t ? (t.readiness as string | null) : undefined,
        sendOutTests: id ? sendOut.get(id) : undefined,
        addressableValue: id ? value.get(id) ?? undefined : undefined,
        completeness: c?.pct,
        missing: c?.missing,
      };
    });
  } catch {
    /* Enrichment is an improvement, not a prerequisite. A failed query here must leave search
       working over the seed rather than emptying the map, which is what a rethrow would do. */
    return base;
  }
}
